import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getOrganizationContext } from '../../orgContext';

interface TokenResponse {
  access_token?: string;
}

export const useAdminApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  const organizationContext = getOrganizationContext();
  const organizationId = organizationContext?.organizationId
    ? String(organizationContext.organizationId)
    : null;
  const hasActiveOrganization = Boolean(organizationId);

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as TokenResponse)?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const getOrganizationHeaders = useCallback(
    (token: string): Record<string, string> => {
      if (!organizationId) {
        throw new Error('No organization selected. Please switch organization and try again.');
      }

      return {
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': organizationId,
      };
    },
    [organizationId]
  );

  return {
    hasActiveOrganization,
    getManagementToken,
    getOrganizationHeaders,
  };
};
